"use client";

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/** A first message for a fresh side chat: `send` starts the conversation, `prefill` waits in the composer. */
export type LauncherPrompt = { text: string; mode: "send" | "prefill" };

export type OpenGailLauncher = (prompt?: LauncherPrompt) => void;

type GailLauncherContextValue = {
  /** Opens the corner side chat, or `null` when no launcher is on screen. */
  openLauncher: OpenGailLauncher | null;
  registerLauncher: (opener: OpenGailLauncher | null) => void;
};

const GailLauncherContext = createContext<GailLauncherContextValue>({
  openLauncher: null,
  registerLauncher: () => {},
});

/** Lets page content open the corner side chat that the launcher owns. */
export function GailLauncherProvider({ children }: { children: React.ReactNode }) {
  const [openLauncher, setOpenLauncher] = useState<OpenGailLauncher | null>(null);
  // Wrapped because a bare function passed to a state setter is called as an updater.
  const registerLauncher = useCallback(
    (opener: OpenGailLauncher | null) => setOpenLauncher(() => opener),
    []
  );
  const value = useMemo(() => ({ openLauncher, registerLauncher }), [openLauncher, registerLauncher]);
  return <GailLauncherContext.Provider value={value}>{children}</GailLauncherContext.Provider>;
}

export const useGailLauncher = (): GailLauncherContextValue => useContext(GailLauncherContext);
